import { useEffect, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid, HardDrive, Users, Cpu, Settings, ShieldCheck, Bug, LogOut, BarChart3, PiggyBank,
  Sparkles, Bot, Search, PanelLeftClose, PanelLeftOpen, ChevronUp,
} from "lucide-react";
import { LogoMark } from "../design/Logo";
import { userStore } from "../../stores/user.store";
import { bugStore } from "../../stores/bug.store";
import { aiStore } from "../../stores/ai.store";
import { fundingStore } from "../../stores/funding.store";
import { serverStore } from "../../stores/server.store";
import { clearToken } from "../../utils/api";
import { cn } from "../../utils/cn";
import { UserAvatar } from "../common/UserAvatar";
import { ServerIcon } from "../server/ServerIcon";
import { can as canUser } from '@/utils/permissions';

const COLLAPSE_KEY = "gm.rail.collapsed";
const NARROW_PX = 1024;
const RUNNING = ["online", "starting", "restarting"];

const readCollapsed = () => {
  try {
    const v = localStorage.getItem(COLLAPSE_KEY);
    if (v !== null) return v === "1";
  } catch { /* privates Fenster o.ä. */ }
  return typeof window !== "undefined" && window.innerWidth < 1280;
};

interface Item {
  id: string;
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}

const Badge = ({ n, floating }: { n: number; floating: boolean }) => (
  <span className={cn(
    "min-w-[16px] h-4 px-1 rounded-full bg-ember text-ink text-[9px] font-semibold flex items-center justify-center",
    floating && "absolute top-0.5 right-0.5",
  )}>
    {n > 99 ? "99+" : n}
  </span>
);

const RailItem = ({ item, collapsed }: { item: Item; collapsed: boolean }) => (
  <button
    onClick={item.onClick}
    aria-label={item.label}
    aria-current={item.active ? "page" : undefined}
    className={cn(
      "group relative flex items-center rounded-xl transition-colors shrink-0",
      collapsed ? "w-11 h-11 justify-center" : "w-full h-10 gap-3 px-3",
      item.active ? "bg-ember/[0.08] text-fg" : "text-fg-muted hover:text-fg hover:bg-white/[0.05]",
    )}
  >
    <span className={cn("shrink-0 transition-colors", item.active && "text-ember")}>{item.icon}</span>
    {!collapsed && <span className="flex-1 text-left text-sm truncate">{item.label}</span>}
    {item.active && <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-5 bg-ember rounded-r-full" />}
    {item.badge !== undefined && item.badge > 0 && <Badge n={item.badge} floating={collapsed} />}
    {collapsed && (
      <span className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded-md bg-surface-2 border border-line text-xs text-fg whitespace-nowrap opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all z-50 shadow-lg">
        {item.label}
      </span>
    )}
  </button>
);

const Group = ({ label, collapsed, children }: { label?: string; collapsed: boolean; children: ReactNode }) => (
  <div className={cn("flex flex-col gap-0.5", collapsed ? "items-center" : "items-stretch")}>
    {label && (collapsed
      ? <div className="w-6 h-px bg-line my-2" aria-hidden />
      : <div className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.1em] text-fg-muted/70 font-medium">{label}</div>)}
    {children}
  </div>
);

/**
 * Hauptnavigation. Ausgeklappt mit Namen und Gruppen (Jack: „so einfach, dass jeder
 * Idiot es nutzen kann" – reine Symbole musste man erraten), eingeklappt wie früher
 * nur Symbole mit Tooltip. Zeigt laufende Server zum direkten Hinspringen.
 */
export const Rail = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = userStore;
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < NARROW_PX);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW_PX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!currentUser) return null;
  const collapsed = narrow || collapsedPref;

  const toggleCollapsed = () => {
    const next = !collapsedPref;
    setCollapsedPref(next);
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* egal */ }
  };

  const can = (p: string) => canUser(currentUser, p);
  const path = location.pathname;
  const go = (to: string) => () => navigate(to);
  const item = (id: string, icon: ReactNode, label: string, to: string, active: boolean, badge?: number): Item =>
    ({ id, icon, label, active, onClick: go(to), badge });

  const overview: Item[] = [
    item("servers", <LayoutGrid size={19} />, "Server", "/", path === "/"),
    ...(can("can_view_player_stats") ? [item("stats", <BarChart3 size={19} />, "Statistik", "/stats", path.startsWith("/stats"))] : []),
  ];
  const admin: Item[] = [
    ...(can("can_manage_storage") ? [item("storage", <HardDrive size={19} />, "Speicher", "/storage", path.startsWith("/storage"))] : []),
    ...(can("can_manage_users") ? [item("users", <Users size={19} />, "Benutzer", "/users", path.startsWith("/users"))] : []),
    ...(can("can_root_reboot") || can("can_manage_maintenance") || can("can_manage_system")
      ? [item("system", <Cpu size={19} />, "System", "/system", path.startsWith("/system"))] : []),
    ...(can("can_view_audit_logs") ? [item("audit", <ShieldCheck size={19} />, "Audit-Log", "/audit", path.startsWith("/audit"))] : []),
  ];
  const community: Item[] = [
    item("funding", <PiggyBank size={19} />, "Server-Kasse", "/funding", path.startsWith("/funding"), fundingStore.openPollsUnvoted),
  ];
  const help: Item[] = [
    ...(can("can_view_ai_dashboard") ? [item("ai", <Sparkles size={19} />, "KI-Diagnosen", "/ai-dashboard", path.startsWith("/ai-dashboard"))] : []),
    { id: "assistant", icon: <Bot size={19} />, label: "KI-Assistent", active: aiStore.open, onClick: () => aiStore.toggle() },
    item("bugs", <Bug size={19} />, "Fehler melden", "/bugs", path.startsWith("/bugs"), bugStore.activeBugCount),
    item("settings", <Settings size={19} />, "Einstellungen", "/settings", path.startsWith("/settings")),
  ];

  const running = serverStore.servers
    .filter((s) => RUNNING.includes(s.status))
    .sort((a, b) => a.name.localeCompare(b.name));

  const openSearch = () => window.dispatchEvent(new CustomEvent("gm:open-palette"));

  return (
    <nav
      className={cn(
        "shrink-0 bg-surface border-r border-line flex flex-col py-4 z-30 transition-[width] duration-200",
        collapsed ? "w-16 items-center" : "w-60 px-3",
      )}
      aria-label="Hauptnavigation"
    >
      <button onClick={go("/")} aria-label="GameManager" className={cn("mb-3 flex items-center gap-2.5", !collapsed && "px-2")}>
        <LogoMark size={30} />
        {!collapsed && (
          <span className="font-display font-semibold tracking-tight text-fg text-[17px]">
            Game<span className="text-ember">Manager</span>
          </span>
        )}
      </button>

      {collapsed ? (
        <RailItem collapsed item={{ id: "search", icon: <Search size={19} />, label: "Suchen (Strg+K)", active: false, onClick: openSearch }} />
      ) : (
        <button
          onClick={openSearch}
          className="mb-1 flex items-center gap-2.5 h-9 px-3 rounded-lg bg-black/25 border border-line text-sm text-fg-muted hover:text-fg hover:border-line-strong transition-colors"
        >
          <Search size={15} />
          <span className="flex-1 text-left">Suchen …</span>
          <kbd className="text-[10px] font-mono text-fg-muted/80 border border-line rounded px-1 py-px">Strg K</kbd>
        </button>
      )}

      <div className={cn("flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-none flex flex-col w-full", collapsed && "items-center")}>
        <Group collapsed={collapsed}>
          {overview.map((i) => <RailItem key={i.id} item={i} collapsed={collapsed} />)}
        </Group>

        {!collapsed && running.length > 0 && (
          <Group label="Laufen gerade" collapsed={collapsed}>
            {running.map((s) => {
              const active = path.startsWith(`/server/${s.id}`);
              return (
                <button
                  key={s.id}
                  onClick={go(`/server/${s.id}/overview`)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative flex items-center gap-2.5 h-9 px-3 rounded-lg text-sm transition-colors",
                    active ? "bg-ember/[0.08] text-fg" : "text-fg-muted hover:text-fg hover:bg-white/[0.05]",
                  )}
                >
                  <ServerIcon serverId={s.id} type={s.type} size={20} className="rounded-md" />
                  <span className="flex-1 text-left truncate">{s.name}</span>
                  <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", s.status === "online" ? "bg-online gm-live-dot" : "bg-warn")} />
                  {active && <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-5 bg-ember rounded-r-full" />}
                </button>
              );
            })}
          </Group>
        )}

        {admin.length > 0 && (
          <Group label="Verwaltung" collapsed={collapsed}>
            {admin.map((i) => <RailItem key={i.id} item={i} collapsed={collapsed} />)}
          </Group>
        )}

        <Group label="Community" collapsed={collapsed}>
          {community.map((i) => <RailItem key={i.id} item={i} collapsed={collapsed} />)}
        </Group>
      </div>

      <div className={cn("flex flex-col gap-0.5 pt-3 border-t border-line w-full", collapsed ? "items-center" : "items-stretch")}>
        {help.map((i) => <RailItem key={i.id} item={i} collapsed={collapsed} />)}

        {!narrow && (
          <RailItem
            collapsed={collapsed}
            item={{
              id: "collapse",
              icon: collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />,
              label: collapsed ? "Leiste ausklappen" : "Einklappen",
              active: false,
              onClick: toggleCollapsed,
            }}
          />
        )}

        <div className="relative mt-1">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Konto"
            className={cn(
              "flex items-center gap-2.5 rounded-xl transition-colors",
              collapsed ? "hover:opacity-80" : "w-full p-1.5 hover:bg-white/[0.05]",
            )}
          >
            {/* MC-Kopf (falls im Profil gesetzt), sonst farbige Initialen */}
            <UserAvatar user={currentUser} size={collapsed ? 36 : 32} rounded="rounded-xl" />
            {!collapsed && (
              <>
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-sm text-fg truncate">{currentUser.username}</span>
                  <span className="block text-[11px] text-fg-muted">{currentUser.role}</span>
                </span>
                <ChevronUp size={15} className={cn("text-fg-muted transition-transform", !menuOpen && "rotate-180")} />
              </>
            )}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className={cn(
                "absolute z-50 w-52 bg-surface-2 border border-line rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150",
                collapsed ? "bottom-0 left-full ml-3" : "bottom-full mb-2 left-0 right-0 w-auto",
              )}>
                <div className="px-3.5 py-3 border-b border-line">
                  <div className="text-sm font-medium text-fg truncate">{currentUser.username}</div>
                  <div className="text-[11px] text-fg-muted uppercase tracking-wider">{currentUser.role}</div>
                </div>
                <button
                  onClick={() => { navigate("/settings"); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-fg hover:bg-white/[0.05] transition-colors"
                >
                  <Settings size={16} className="text-fg-muted" /> Mein Profil
                </button>
                <button
                  onClick={() => { clearToken(); userStore.currentUser = null; }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <LogOut size={16} /> Abmelden
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
});
